/**
 * Cria um delay cancelável usando AbortController
 *
 * @param ms - Tempo em milissegundos para o delay
 * @param controller - AbortController para cancelar o delay
 * @returns Promise que resolve após o delay ou rejeita se cancelada
 *
 * @example
 * ```typescript
 * const controller = new AbortController();
 *
 * try {
 *   await createCancellableDelay(1000, controller);
 *   console.log('Delay completed');
 * } catch (error) {
 *   if (error.message === 'Animation cancelled') {
 *     console.log('Delay was cancelled');
 *   }
 * }
 * ```
 */
export function createCancellableDelay(
	ms: number,
	controller: AbortController
): Promise<void> {
	return new Promise((resolve, reject) => {
		let isSettled = false;

		const cleanup = () => {
			controller.signal.removeEventListener('abort', onAbort);
		};

		const onAbort = () => {
			if (isSettled) return;
			isSettled = true;
			cleanup();
			clearTimeout(timeoutId);
			reject(new Error('Animation cancelled'));
		};

		const timeoutId = setTimeout(() => {
			if (isSettled) return;
			isSettled = true;
			cleanup();
			if (!controller.signal.aborted) {
				resolve();
			}
		}, ms);

		// Se já foi abortado antes de adicionar o listener
		if (controller.signal.aborted) {
			clearTimeout(timeoutId);
			reject(new Error('Animation cancelled'));
			return;
		}

		controller.signal.addEventListener('abort', onAbort);
	});
}

/**
 * Cria e gerencia um AbortController para animações sequenciais.
 * Cancela automaticamente a animação anterior quando uma nova é iniciada.
 *
 * @returns Objeto com funções para iniciar nova animação e cancelar todas
 *
 * @example
 * ```typescript
 * const animationManager = createAnimationManager();
 *
 * const runAnimation = async () => {
 *   const controller = animationManager.startNew();
 *
 *   try {
 *     await createCancellableDelay(300, controller);
 *     // Próximo passo da animação...
 *     await createCancellableDelay(500, controller);
 *   } catch (error) {
 *     if (error.message === 'Animation cancelled') {
 *       // Animação foi cancelada
 *     }
 *   }
 * };
 * ```
 */
export function createAnimationManager() {
	let currentController: AbortController | null = null;

	return {
		/**
		 * Inicia uma nova animação, cancelando a anterior se existir
		 */
		startNew(): AbortController {
			// Cancela a animação anterior se existir
			if (currentController) {
				currentController.abort();
			}

			// Cria um novo controller para a nova animação
			currentController = new AbortController();
			return currentController;
		},

		/**
		 * Cancela a animação atual se estiver em execução
		 */
		cancelCurrent(): void {
			if (currentController) {
				currentController.abort();
				currentController = null;
			}
		},

		/**
		 * Verifica se há uma animação em execução
		 */
		get isAnimating(): boolean {
			return currentController !== null && !currentController.signal.aborted;
		}
	};
}

/**
 * Hook para gerenciar animações canceláveis em componentes Vue
 * Automaticamente limpa as animações quando o componente é desmontado
 *
 * @returns Objeto com funções para gerenciar animações
 *
 * @example
 * ```vue
 * <script setup>
 * const { startAnimation, createDelay, isAnimating } = useAnimationManager();
 *
 * const animateHeader = async () => {
 *   const controller = startAnimation();
 *
 *   try {
 *     await createDelay(300, controller);
 *     // Animação...
 *   } catch (error) {
 *     // Tratamento de cancelamento
 *   }
 * };
 * </script>
 * ```
 */
export function useAnimationManager() {
	const manager = createAnimationManager();

	// Cleanup automático quando o componente for desmontado
	const { $nuxt } = useNuxtApp();
	onUnmounted(() => {
		manager.cancelCurrent();
	});

	return {
		/**
		 * Inicia uma nova animação
		 */
		startAnimation: manager.startNew,

		/**
		 * Cancela a animação atual
		 */
		cancelAnimation: manager.cancelCurrent,

		/**
		 * Cria um delay cancelável
		 */
		createDelay: createCancellableDelay,

		/**
		 * Verifica se há animação em execução
		 */
		isAnimating: computed(() => manager.isAnimating)
	};
}
